import type { LLMAnalysisResultItem } from "src/modules/ANNModule/ANNLLMAnalyzer";
import {
   collectFieldNotes,
   getAiFieldText,
   getRowFieldText,
   hydrateAnalysisItemFromPaataleNote,
   isFieldNoteAccepted,
   SERVITUT_ANALYSIS_FIELDS,
   type ServitutAnalysisFieldKey,
} from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import {
   findServitutDocumentAnnotation,
   hydrateAnalysisItemFromAnnotation,
   normalizeDatoloebenrValue,
   pickStedfaestelseForRow,
} from "src/modules/ANNModule/ANNServitutDocumentAnnotations";
import type { ANNAnnotationRecord } from "src/modules/ANNModule/ANNTypes";
import {
   statusFromConfidence,
   type ServitutAiFieldStatus,
} from "src/modules/ANNModule/ANNServitutProvenance";
import { findIgnoreNote } from "./ignore";
import { rowDocumentId, rowDocumentIds } from "./identity";
import { emptyPageModel, normalizeAktPages, pageModelFromLists, type PageExtent } from "./pages";
import {
   digitalOpenBinding,
   emptyFields,
   isDigitalRow,
   isFetched,
   isUserFileDocument,
   withNativeDigitalAcceptance,
   type DocumentFileOrigin,
   type FieldSlot,
   type PreparedAktSource,
   type ServitutRow,
} from "./row";

export type ProjectAnnOptions = {
   hasCaseDescription?: boolean;
   /** Live ANN updates must not clobber an uncommitted upload or page edit. */
   protectUncommitted?: boolean;
   /** Only a complete ANN list may treat a missing Document or note as deleted. */
   recordsAreComplete?: boolean;
};

function firstText(...values: Array<string | null | undefined>): string | undefined {
   for (const value of values) {
      if (String(value || "").trim()) {
         return String(value);
      }
   }
   return undefined;
}

/** Live session facts win; a thinner stored Document only fills gaps. */
export function mergeAnalysisItems(
   live?: LLMAnalysisResultItem,
   stored?: LLMAnalysisResultItem,
): LLMAnalysisResultItem | undefined {
   if (!live) {
      return stored;
   }
   if (!stored) {
      return live;
   }
   return {
      ...stored,
      ...live,
      summary: firstText(live.summary, stored.summary) ?? live.summary,
      servitut_type: live.servitut_type || stored.servitut_type,
      servitut_type_confidence: live.servitut_type ? live.servitut_type_confidence : stored.servitut_type_confidence,
      paataleberettiget: firstText(live.paataleberettiget, stored.paataleberettiget),
      paataleberettiget_confidence: live.paataleberettiget ? live.paataleberettiget_confidence : stored.paataleberettiget_confidence,
      paataleberettiget_resolution: live.paataleberettiget_resolution || stored.paataleberettiget_resolution,
      case_relevant: live.case_relevant !== undefined ? live.case_relevant : stored.case_relevant,
      case_relevance_reasoning: firstText(live.case_relevance_reasoning, stored.case_relevance_reasoning),
      case_relevance_confidence: live.case_relevance_reasoning ? live.case_relevance_confidence : stored.case_relevance_confidence,
      case_description: firstText(live.case_description, stored.case_description) ?? live.case_description,
      overall_confidence: live.summary ? live.overall_confidence : stored.overall_confidence,
      pages_kept: live.pages_kept?.length ? live.pages_kept : stored.pages_kept,
   };
}

/** A persisted Document is Modal A work already accepted, unless ANN marked it ignored. */
function reviewStateFromAnnDocument(
   document?: { document?: { provenance?: { reviewState?: ServitutRow["review"]["state"] } } } | null,
): ServitutRow["review"]["state"] | undefined {
   if (!document) {
      return undefined;
   }
   if (document.document?.provenance?.reviewState === "rejected") {
      return "rejected";
   }
   return "checked";
}

function reviewFromAnnDocument(
   row: ServitutRow,
   document: { document?: { provenance?: { reviewState?: ServitutRow["review"]["state"]; origin?: ServitutRow["review"]["origin"] } } },
): ServitutRow["review"] {
   const provenance = document.document?.provenance;
   const annState = reviewStateFromAnnDocument(document) || "checked";
   if (row.review.state === "checked" && annState !== "rejected") {
      return row.review;
   }
   return {
      state: annState,
      origin: provenance?.origin || row.review.origin || "person",
   };
}

export function annotationOriginFromRecord(
   row: ServitutRow,
   provenanceSource?: "akt" | "file",
): DocumentFileOrigin {
   if (provenanceSource === "file" || row.document.kind === "upload") {
      return "upload";
   }
   if (row.document.kind === "annotation" && row.document.origin) {
      return row.document.origin;
   }
   if (isDigitalRow(row)) {
      return "digital";
   }
   return "akt";
}

function preparedAktSource(value?: string): PreparedAktSource | undefined {
   if (value === "korrektAkt" || value === "servitutAkt" || value === "textAkt" || value === "ovrigAkt" || value === "customAkt") {
      return value;
   }
   return undefined;
}

function lookedAtAktFromSavedDocument(
   origin: DocumentFileOrigin,
   filename?: string,
   provenance?: { aktId?: string; usedSource?: string },
): { aktId?: string; source?: PreparedAktSource; filename?: string } {
   if (origin === "upload") {
      return { filename };
   }
   const filenameLooksLikePdf = Boolean(filename?.toLowerCase().endsWith(".pdf"));
   const aktId = provenance?.aktId || (!filenameLooksLikePdf ? filename : undefined);
   return {
      aktId,
      source: preparedAktSource(provenance?.usedSource),
      filename: filename || aktId,
   };
}

function shouldProtectSessionDocument(
   row: ServitutRow,
   annReview: "unchecked" | "checked" | "rejected" | undefined,
   protectUncommitted: boolean,
): boolean {
   if (row.document.kind === "upload" && row.review.state !== "checked") {
      return true;
   }
   if (!protectUncommitted) {
      return false;
   }
   if (row.job !== "idle") {
      return true;
   }
   if (row.review.state === "rejected" && annReview !== "rejected") {
      return true;
   }
   return row.review.state === "unchecked" && (annReview === "checked" || annReview === "rejected");
}

export function recordsForRow(row: ServitutRow, records: ANNAnnotationRecord[]): ANNAnnotationRecord[] {
   const ids = new Set(rowDocumentIds(row));
   const dato = normalizeDatoloebenrValue(row.identity.datoloebenr);
   return records.filter((record) => {
      const recordDocumentId = String(record.documentId || "");
      if (recordDocumentId && ids.has(recordDocumentId)) {
         return true;
      }
      if (recordDocumentId && ids.size) {
         return false;
      }
      return Boolean(dato && normalizeDatoloebenrValue(record.datoloebenr) === dato);
   });
}

function fieldStatusFromAnalysis(
   item: LLMAnalysisResultItem | undefined,
   field: ServitutAnalysisFieldKey,
   canRun: boolean,
   hasCaseDescription: boolean,
): ServitutAiFieldStatus {
   if (field === "resume") {
      return statusFromConfidence(item?.overall_confidence, false, Boolean(String(item?.summary || "").trim()), canRun);
   }
   if (field === "entitledParty") {
      return statusFromConfidence(
         item?.paataleberettiget_confidence,
         false,
         Boolean(item?.paataleberettiget || item?.paataleberettiget_resolution),
         canRun,
      );
   }
   if (field === "condition") {
      return statusFromConfidence(item?.servitut_type_confidence, false, Boolean(item?.servitut_type), canRun);
   }
   const analyzedStatement = Boolean(item?.case_description?.trim())
      || item?.case_relevant !== undefined
      || Boolean(item?.case_relevance_reasoning);
   if (!hasCaseDescription && !analyzedStatement) {
      return canRun ? "not_run" : "cannot_run";
   }
   return statusFromConfidence(
      item?.case_relevance_confidence,
      false,
      item?.case_relevant !== undefined || Boolean(item?.case_relevance_reasoning),
      canRun,
   );
}

function overlayAcceptedNotes(
   fields: ServitutRow["fields"],
   notes: ReturnType<typeof collectFieldNotes>,
   replaceMissing: boolean,
): ServitutRow["fields"] {
   const next = { ...fields };
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      const note = notes[field];
      if (isFieldNoteAccepted(note)) {
         next[field] = {
            ...next[field],
            aiStatus: "person_approved",
            acceptedNoteId: note?.id,
            acceptedText: (note?.note?.text || "").trim(),
         };
         return;
      }
      if (replaceMissing) {
         next[field] = {
            ...next[field],
            acceptedNoteId: undefined,
            acceptedText: undefined,
         };
      }
   });
   return next;
}

export function fieldsFromAnalysis(
   row: ServitutRow,
   item: LLMAnalysisResultItem | undefined,
   options?: { hasCaseDescription?: boolean },
): ServitutRow["fields"] {
   const canRun = isFetched(row);
   const hasCase = options?.hasCaseDescription !== false;
   const next = { ...row.fields };
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      const accepted = row.fields[field];
      const incomingText = getAiFieldText(field, item, { caseDescription: item?.case_description || undefined });
      next[field] = {
         aiStatus: accepted.acceptedNoteId
            ? "person_approved"
            : fieldStatusFromAnalysis(item, field, canRun, hasCase),
         aiText: incomingText || accepted.aiText || "",
         acceptedNoteId: accepted.acceptedNoteId,
         acceptedText: accepted.acceptedText,
      };
   });
   return next;
}

function applyAnalysisToRow(
   row: ServitutRow,
   item: LLMAnalysisResultItem | undefined,
   notes: ReturnType<typeof collectFieldNotes>,
   options?: ProjectAnnOptions,
   applyNotes?: boolean,
): ServitutRow {
   if (!item) {
      return row;
   }
   let next: ServitutRow = {
      ...row,
      analysis: { item, stale: Boolean(row.analysis?.stale) },
      fields: fieldsFromAnalysis({ ...row, analysis: { item, stale: Boolean(row.analysis?.stale) } }, item, options),
   };
   if (applyNotes) {
      next = {
         ...next,
         fields: overlayAcceptedNotes(next.fields, notes, Boolean(options?.recordsAreComplete)),
      };
   }
   return next;
}

export function projectAnnOntoRow(
   row: ServitutRow,
   records: ANNAnnotationRecord[],
   options?: ProjectAnnOptions,
): ServitutRow {
   const scoped = recordsForRow(row, records);
   const documentId = rowDocumentId(row);
   const document = findServitutDocumentAnnotation(scoped, row.identity.datoloebenr);
   const notes = collectFieldNotes(scoped, row.identity.datoloebenr);
   const stedfaestelse = pickStedfaestelseForRow(scoped, {
      documentId,
      datoloebenr: row.identity.datoloebenr,
   });
   const annReview = reviewStateFromAnnDocument(document);
   const protectDocument = shouldProtectSessionDocument(row, annReview, Boolean(options?.protectUncommitted));
   const pagesInvalidated = row.review.state === "unchecked" && Boolean(row.analysis?.stale);
   const applyNotes = row.document.kind !== "upload" && !pagesInvalidated;
   const fields = applyNotes
      ? overlayAcceptedNotes({ ...row.fields }, notes, Boolean(options?.recordsAreComplete))
      : row.fields;

   let next: ServitutRow = {
      ...row,
      fields,
      stedfaestelseId: stedfaestelse?.id || row.stedfaestelseId,
   };

   if (protectDocument) {
      return withNativeDigitalAcceptance(applyAnalysisToRow(next, next.analysis?.item, notes, options, applyNotes));
   }

   if (document?.document?.s3Key) {
      const extracted = normalizeAktPages(document.document.extractedPages);
      const provenance = document.document.provenance;
      const stored = hydrateAnalysisItemFromPaataleNote(
         hydrateAnalysisItemFromAnnotation(document, row.id),
         notes.entitledParty,
      );
      const merged = mergeAnalysisItems(row.analysis?.item, stored);
      const keepDigital = isDigitalRow(row) && !isUserFileDocument(row) && provenance?.source !== "file";
      const digital = digitalOpenBinding(row);
      const origin = annotationOriginFromRecord(row, provenance?.source);
      const lookedAt = lookedAtAktFromSavedDocument(
         origin,
         document.document.filename
            || (row.document.kind === "upload" || row.document.kind === "annotation" ? row.document.filename : undefined),
         provenance,
      );
      const filename = lookedAt.filename;
      const extent: PageExtent = keepDigital && digital
         ? "entire_akt"
         : (extracted.length ? "selection" : "missing");
      next = {
         ...next,
         document: keepDigital && digital
            ? digital
            : {
               kind: "annotation",
               recordId: document.id,
               s3Key: document.document.s3Key,
               extractedAktPages: extracted,
               filename,
               s3Url: document.document.s3Url,
               mimeType: document.document.mimeType,
               size: document.document.size,
               origin,
               aktId: lookedAt.aktId,
               source: lookedAt.source,
            },
         registryBinding: digital || next.registryBinding,
         pdfStatus: "ready",
         pages: keepDigital && digital
            ? pageModelFromLists([], [], "entire_akt")
            : pageModelFromLists(
               extracted.length ? extracted : (merged?.pages_kept || provenance?.aiPagesKept),
               provenance?.aiPagesKept || merged?.pages_kept,
               extent,
            ),
         review: reviewFromAnnDocument(row, document),
         analysis: merged
            ? { item: merged, stale: Boolean(row.analysis?.stale) }
            : next.analysis,
         sourceMessage: next.sourceMessage || (keepDigital && digital ? undefined : "Using saved annotation PDF"),
      };
   } else if (options?.recordsAreComplete && row.document.kind === "annotation") {
      const restored = row.registryBinding;
      next = {
         ...next,
         document: restored || { kind: "none" },
         registryBinding: restored,
         pdfStatus: restored ? "ready" : "pending",
         pages: emptyPageModel(),
         review: { state: "unchecked" },
         analysis: undefined,
         sourceMessage: undefined,
      };
   }

   const analysisItem = next.analysis?.item;
   if (analysisItem) {
      const item = hydrateAnalysisItemFromPaataleNote(analysisItem, notes.entitledParty) || analysisItem;
      next = applyAnalysisToRow({
         ...next,
         analysis: { item, stale: Boolean(next.analysis?.stale) },
      }, item, notes, options, applyNotes);
   }

   return withNativeDigitalAcceptance(applyIgnoreFromAnn(row, next, scoped, document, options));
}

function applyIgnoreFromAnn(
   row: ServitutRow,
   next: ServitutRow,
   records: ANNAnnotationRecord[],
   document: ReturnType<typeof findServitutDocumentAnnotation>,
   options?: ProjectAnnOptions,
): ServitutRow {
   const ignoreNote = findIgnoreNote(records, row.identity.datoloebenr);
   const documentRejected = document?.document?.provenance?.reviewState === "rejected";
   const annIgnored = Boolean(ignoreNote || documentRejected);
   if (options?.protectUncommitted && row.review.state !== "rejected" && annIgnored) {
      return next;
   }
   if (annIgnored) {
      return { ...next, review: { state: "rejected", origin: ignoreNote ? "person" : (document?.document?.provenance?.origin || row.review.origin || "person") } };
   }
   if (options?.recordsAreComplete && !options?.protectUncommitted && next.review.state === "rejected" && !documentRejected) {
      return { ...next, review: { state: "unchecked" } };
   }
   return next;
}

export function fieldDisplayText(
   row: ServitutRow,
   field: ServitutAnalysisFieldKey,
   caseDescription?: string,
   options?: { preferLiveAi?: boolean },
): string {
   return getRowFieldText({
      analysisItem: row.analysis?.item,
      summary: row.landRegistry.summary,
      fieldAnnotations: Object.fromEntries(
         SERVITUT_ANALYSIS_FIELDS.map((key) => [
            key,
            row.fields[key].acceptedNoteId
               ? { verified: true, note: { comment: "", text: row.fields[key].acceptedText || "" } }
               : undefined,
         ]),
      ) as never,
      fieldAiTexts: Object.fromEntries(
         SERVITUT_ANALYSIS_FIELDS.map((key) => [key, row.fields[key].aiText]),
      ),
      provenance: { analysisStale: row.analysis?.stale },
   }, field, caseDescription, options);
}

export function rebuildFieldSlot(
   previous: FieldSlot,
   item: LLMAnalysisResultItem | undefined,
   field: ServitutAnalysisFieldKey,
   canRun: boolean,
   hasCaseDescription: boolean,
): FieldSlot {
   const accepted = Boolean(previous.acceptedNoteId && (previous.acceptedText || "").trim());
   return {
      aiStatus: accepted ? "person_approved" : fieldStatusFromAnalysis(item, field, canRun, hasCaseDescription),
      aiText: getAiFieldText(field, item, { caseDescription: item?.case_description || undefined }) || previous.aiText,
      acceptedNoteId: previous.acceptedNoteId,
      acceptedText: previous.acceptedText,
   };
}

export function isResolvableHistoricalPaatale(name?: string): boolean {
   const historicalName = String(name || "").trim();
   if (!historicalName) {
      return false;
   }
   const normalized = historicalName
      .normalize("NFKC")
      .toLocaleLowerCase("da-DK")
      .replace(/[.,;:!?]+$/g, "")
      .trim();
   const missingValues = new Set([
      "",
      "-",
      "ingen",
      "ingen påtaleberettiget",
      "ikke angivet",
      "ikke fundet",
      "ikke identificeret",
      "ikke oplyst",
      "n/a",
      "null",
      "ukendt",
      "undefined",
   ]);
   return !missingValues.has(normalized) && !normalized.includes("påtaleberettiget ikke fundet");
}

export function historicalPaataleName(row: {
   analysis?: { item?: LLMAnalysisResultItem };
   analysisItem?: LLMAnalysisResultItem;
   fields?: ServitutRow["fields"];
}): string {
   const item = row.analysis?.item || row.analysisItem;
   return (
      item?.paataleberettiget
      || item?.paataleberettiget_resolution?.historical_name
      || ""
   ).trim();
}
